import { productTypes, type useAllProductsOptions } from "./hooks";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import cn from "mcn";
import { createPortal } from "react-dom";
import { useDeepCompareEffect, useWindowSize } from "@react-hookz/web";
import { Button, Checkbox } from "../ui";
import { CloseIcon } from "../icons/CloseIcon";
import { FaChevronDown, FaChevronUp, FaXmark } from "react-icons/fa6";
import { objectEntries } from "../../utils";
import ClickAwayListener from "../components/ClickAwayListener";

const FILTER_WIDTH = 216;

const keyToLabel = {
  types: "Product type",
  regions: "Region",
  commodities: "Commodity",
};

export type TCurveFilter = {
  types: string[];
  regions: string[];
  commodities: string[];
};

export function CurveFilterProvider({ children }: { children: React.ReactNode }) {
  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<null | {
    top: number;
    right: number;
  }>(null);

  const [showTypes, setShowTypes] = useState(false);
  const [showRegions, setShowRegions] = useState(false);
  const [showCommodities, setShowCommodities] = useState(false);

  const updatePosition = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    const popoverRect = popoverRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Take window edge (bottom and right) into account
    const attemptedRight = windowWidth - rect.right - FILTER_WIDTH;
    const attemptedTop = rect.bottom + 8;
    const right =
      attemptedRight < 8
        ? 8
        : attemptedRight > windowWidth - FILTER_WIDTH
          ? windowWidth - FILTER_WIDTH - 8
          : attemptedRight;
    const top =
      attemptedTop + (popoverRect?.height ?? FILTER_WIDTH) > windowHeight
        ? Math.max(8, windowHeight - (popoverRect?.height ?? 0) - 16)
        : attemptedTop;
    setPosition({ top, right });
  };

  return (
    <CurveFilterContext.Provider
      value={{
        anchorRef,
        popoverRef,
        position,
        setPosition,
        updatePosition,
        showTypes,
        setShowTypes,
        showRegions,
        setShowRegions,
        showCommodities,
        setShowCommodities,
      }}
    >
      {children}
    </CurveFilterContext.Provider>
  );
}

export function CurveFilter({
  products,
  filters,
  setFilters,
  children,
  rightOffset = 0,
}: {
  products: ReturnType<typeof useAllProductsOptions>["data"];
  filters: TCurveFilter;
  setFilters: React.Dispatch<React.SetStateAction<TCurveFilter>>;
  children: React.ReactNode;
  rightOffset?: number;
}) {
  const { width: windowWidth, height: windowHeight } = useWindowSize();

  // To store the initial filters.
  const [curveFilters, setCurveFilters] = useState<TCurveFilter>(filters);

  const {
    anchorRef,
    popoverRef,
    position,
    setPosition,
    updatePosition,
    showTypes,
    setShowTypes,
    showRegions,
    setShowRegions,
    showCommodities,
    setShowCommodities,
  } = useCurveFilterContext();

  // Sync internal state with external filters when they actually change.
  useDeepCompareEffect(() => {
    setCurveFilters(filters);
  }, [filters]);

  // Update position if window size changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to run this when window size changes, not when position or section visibility changes.
  useEffect(() => {
    if (!position) return;
    updatePosition();
  }, [windowWidth, windowHeight, showTypes, showRegions, showCommodities]);

  const handleClickAway = () => {
    setCurveFilters(filters);
    setPosition(null);
  };

  return (
    <>
      <div
        ref={anchorRef}
        className="cursor-pointer"
        onClick={() => {
          if (position) return setPosition(null);
          updatePosition();
        }}
      >
        {children}
      </div>
      {position &&
        createPortal(
          <ClickAwayListener onClickAway={handleClickAway}>
            <div
              ref={popoverRef}
              className="bg-card border-1 p-4 border-outline rounded-lg shadow-lg text-foreground flex flex-col gap-4 select-none [&_input]:select-text [&_textarea]:select-text"
              style={{
                position: "fixed",
                top: position.top,
                right: Math.max(8, position.right - rightOffset),
                width: FILTER_WIDTH,
                maxHeight: "60vh",
                overflowY: "auto",
                zIndex: 10000,
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-foreground">Filters</span>
                <CloseIcon onClick={() => setPosition(null)} />
              </div>
              <div className="flex flex-col gap-6 p-[3px]">
                <div className="flex flex-col gap-5 pr-4">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => {
                      setShowTypes(!showTypes);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-foreground font-medium">Product type</span>
                      <span className="text-primary font-medium">({curveFilters.types.length})</span>
                    </div>
                    <span className="text-neutral font-medium">{showTypes ? <FaChevronUp /> : <FaChevronDown />}</span>
                  </div>
                  {showTypes && (
                    <FilterSection
                      products={products}
                      section="types"
                      curveFilters={curveFilters}
                      setCurveFilters={setCurveFilters}
                      updatePosition={updatePosition}
                    />
                  )}
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => {
                      setShowRegions(!showRegions);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-foreground font-medium">Region</span>
                      <span className="text-primary font-medium">({curveFilters.regions.length})</span>
                    </div>
                    <span className="text-neutral font-medium">
                      {showRegions ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                  {showRegions && (
                    <FilterSection
                      products={products}
                      section="regions"
                      curveFilters={curveFilters}
                      setCurveFilters={setCurveFilters}
                      updatePosition={updatePosition}
                    />
                  )}
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => {
                      setShowCommodities(!showCommodities);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-foreground font-medium">Commodity</span>
                      <span className="text-primary font-medium">({curveFilters.commodities.length})</span>
                    </div>
                    <span className="text-neutral font-medium ">
                      {showCommodities ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                  {showCommodities && (
                    <FilterSection
                      products={products}
                      section="commodities"
                      curveFilters={curveFilters}
                      setCurveFilters={setCurveFilters}
                      updatePosition={updatePosition}
                    />
                  )}
                </div>
                <div className="flex justify-end gap-4">
                  <Button
                    buttonType="closeSecondary"
                    onClick={() => {
                      setCurveFilters(filters);
                      setPosition(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    buttonType="save"
                    onClick={() => {
                      setFilters(curveFilters);
                      setPosition(null);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </ClickAwayListener>,
          document.body,
        )}
    </>
  );
}

export function CurveFilterPreview({
  filters,
  setFilters,
}: {
  filters: TCurveFilter;
  setFilters: React.Dispatch<React.SetStateAction<TCurveFilter>>;
}) {
  const { updatePosition, setShowTypes, setShowRegions, setShowCommodities } = useCurveFilterContext();

  const openSection = useCallback(
    (type: keyof TCurveFilter) => {
      // Close all sections first.
      setShowTypes(false);
      setShowRegions(false);
      setShowCommodities(false);

      switch (type) {
        case "types":
          setShowTypes(true);
          break;
        case "regions":
          setShowRegions(true);
          break;
        case "commodities":
          setShowCommodities(true);
          break;
      }
    },
    [setShowTypes, setShowRegions, setShowCommodities],
  );

  // If no filters are applied, don't show preview.
  if (!filters.types.length && !filters.regions.length && !filters.commodities.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 py-1 justify-between">
        <span className="text-lg font-semibold text-foreground">Filters Applied:</span>
        <Button
          buttonType="linkButton"
          onClick={() => {
            setFilters({ types: [], regions: [], commodities: [] });
          }}
          className="text-lg"
        >
          Clear All Filters
        </Button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {objectEntries(filters).map(([key, values]) => {
          // If only one value, show as text. Otherwise, show the first and (+N).
          const firstValue = productTypes[values[0]] ?? values[0];
          const text = values.length === 1 ? firstValue : `${firstValue} (+${values.length - 1})`;
          if (!values.length) return null;
          return (
            <Chip
              key={key}
              type={keyToLabel[key]}
              text={text}
              tooltip={values.map((v) => productTypes[v] ?? v).join(", ")}
              onClick={() => {
                updatePosition();
                // Open the relevant section.
                openSection(key);
              }}
              onRemove={() => {
                // Remove all filters of this type.
                setFilters((f) => ({ ...f, [key]: [] }));
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

const CurveFilterContext = createContext<{
  anchorRef: React.RefObject<HTMLDivElement>;
  popoverRef: React.RefObject<HTMLDivElement>;
  position: null | { top: number; right: number };
  setPosition: React.Dispatch<React.SetStateAction<null | { top: number; right: number }>>;
  updatePosition: () => void;
  showTypes: boolean;
  setShowTypes: React.Dispatch<React.SetStateAction<boolean>>;
  showRegions: boolean;
  setShowRegions: React.Dispatch<React.SetStateAction<boolean>>;
  showCommodities: boolean;
  setShowCommodities: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  anchorRef: { current: null },
  popoverRef: { current: null },
  position: null,
  setPosition: () => void null,
  updatePosition: () => void null,
  showTypes: false,
  setShowTypes: () => void null,
  showRegions: false,
  setShowRegions: () => void null,
  showCommodities: false,
  setShowCommodities: () => void null,
});

function useCurveFilterContext() {
  return useContext(CurveFilterContext);
}

function Chip({
  type,
  text,
  tooltip,
  onClick,
  onRemove,
}: {
  type: string;
  text: string;
  tooltip?: string;
  onClick?: () => void;
  onRemove?: () => void;
}) {
  return (
    <div
      className={cn("flex items-center overflow-hidden rounded-full border-1 border-neutral h-[18px] px-[4px]", [
        Boolean(onClick),
        "cursor-pointer hover:bg-neutral-tonal",
      ])}
    >
      <div className="flex gap-1" onClick={() => onClick?.()}>
        <span className="text-[10.5px] font-semibold text-neutral-on-tonal">{type}:</span>
        <TooltipTrigger as="span">
          <span className="text-[10.5px] font-medium mr-1 text-neutral-on-tonal whitespace-nowrap overflow-ellipsis overflow-hidden">
            {text}
          </span>
          <Tooltip>{tooltip}</Tooltip>
        </TooltipTrigger>
      </div>
      {onRemove && (
        <div className="flex gap-1">
          <div onClick={onRemove} className="cursor-pointer text-[10px] text-neutral hover:text-neutral-hover">
            <FaXmark />
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSection({
  products,
  section,
  curveFilters,
  setCurveFilters,
  updatePosition,
}: {
  products: ReturnType<typeof useAllProductsOptions>["data"];
  section: keyof TCurveFilter;
  curveFilters: TCurveFilter;
  setCurveFilters: React.Dispatch<React.SetStateAction<TCurveFilter>>;
  updatePosition?: () => void;
}) {
  // Update position when the section is shown/hidden, and on mount.
  useEffect(() => {
    if (updatePosition) updatePosition();
  }, [updatePosition]);

  return (
    <div className="flex flex-col gap-2 pl-3">
      {products[section].map((s) => {
        return (
          <Checkbox
            key={s.value}
            label={s.label}
            checked={curveFilters[section].includes(s.value)}
            onChange={(e) => {
              const checked = e.target.checked;
              setCurveFilters((f) => {
                const updated = checked ? [...f[section], s.value] : f[section].filter((t) => t !== s.value);
                return { ...f, [section]: updated };
              });
            }}
          />
        );
      })}
    </div>
  );
}
